'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { eventWorkflowApi, eventsApi, organizerExperienceApi, type OrganizerExperiencePayload } from '@/lib/api';
import { clearWorkspaceSessionContext, isValidEventWorkspaceId } from '@/lib/resolve-static-param';

export type EventWorkspaceEvent = Record<string, unknown> & {
  id?: string;
  title?: string;
  /** Public URL slug used for /events/{slug} */
  slug?: string;
  /** Raw backend lifecycle status (draft, published, …) */
  status?: string;
  event_feature?: Record<string, unknown>;
};

type EventWorkspaceContextValue = {
  eventId: string;
  event: EventWorkspaceEvent | null;
  experience: OrganizerExperiencePayload | null;
  loading: boolean;
  experienceLoading: boolean;
  error: string | null;
  experienceError: string | null;
  refetch: () => Promise<void>;
  refetchExperience: () => Promise<void>;
};

const EventWorkspaceContext = createContext<EventWorkspaceContextValue | null>(null);

export function EventWorkspaceProvider({ eventId, children }: { eventId: string; children: ReactNode }) {
  const [event, setEvent] = useState<EventWorkspaceEvent | null>(null);
  const [experience, setExperience] = useState<OrganizerExperiencePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [experienceLoading, setExperienceLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [experienceError, setExperienceError] = useState<string | null>(null);

  const loadEvent = useCallback(async () => {
    if (!isValidEventWorkspaceId(eventId)) {
      setEvent(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await eventWorkflowApi.getDraft(eventId);
      setEvent((res.data?.data?.event ?? null) as EventWorkspaceEvent | null);
    } catch (draftErr: unknown) {
      const draftStatus = (draftErr as { response?: { status?: number } })?.response?.status;
      // Legacy / missing-draft events: fall back to organizer show so workspace still renders.
      try {
        const res = await eventsApi.showEvent(eventId);
        const payload = res.data?.data ?? res.data ?? null;
        setEvent((payload as EventWorkspaceEvent | null) ?? null);
        setError(null);
      } catch (showErr: unknown) {
        const showStatus = (showErr as { response?: { status?: number } })?.response?.status;
        const status = showStatus ?? draftStatus;
        setEvent(null);
        if (status === 403) {
          setError('You do not have permission to access this event.');
          clearWorkspaceSessionContext();
        } else if (status === 404) {
          setError('Event not found.');
        } else if (status === 401) {
          setError('Authentication required.');
        } else {
          setError('Could not load event');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  const loadExperience = useCallback(async () => {
    if (!isValidEventWorkspaceId(eventId)) {
      setExperience(null);
      setExperienceLoading(false);
      setExperienceError(null);
      return;
    }
    setExperienceLoading(true);
    setExperienceError(null);
    try {
      const res = await organizerExperienceApi.getExperience(eventId);
      setExperience(res.data?.data ?? null);
    } catch {
      // Experience is optional enrichment — do not block Participants / other workspace pages.
      setExperience(null);
      setExperienceError(null);
    } finally {
      setExperienceLoading(false);
    }
  }, [eventId]);

  const refetch = useCallback(async () => {
    await Promise.all([loadEvent(), loadExperience()]);
  }, [loadEvent, loadExperience]);

  useEffect(() => {
    loadEvent();
    loadExperience();
  }, [loadEvent, loadExperience]);

  const value = useMemo(
    () => ({
      eventId,
      event,
      experience,
      loading,
      experienceLoading,
      error,
      experienceError,
      refetch,
      refetchExperience: loadExperience,
    }),
    [eventId, event, experience, loading, experienceLoading, error, experienceError, refetch, loadExperience]
  );

  return <EventWorkspaceContext.Provider value={value}>{children}</EventWorkspaceContext.Provider>;
}

export function useEventWorkspace(): EventWorkspaceContextValue {
  const ctx = useContext(EventWorkspaceContext);
  if (!ctx) {
    throw new Error('useEventWorkspace must be used within EventWorkspaceProvider');
  }
  return ctx;
}

/** Safe variant for optional use outside strict shell (returns null if no provider). */
export function useEventWorkspaceOptional(): EventWorkspaceContextValue | null {
  return useContext(EventWorkspaceContext);
}
